/**
 * @module core
 * @description Express app, middleware, abilities, events, and utilities
 *
 * @dependencies
 * - db/ (connection, seed-runner, ensure-system-tables)
 * - engine/ (loader, getOrderedModules, context, registry)
 * - runtime/ (cache, entity-factory)
 *
 * @note This is the orchestration layer that wires together all other modules.
 *       It depends on db/, engine/, and runtime/ to start the server.
 */

// Logger
export { logger, createChildLogger, setLoggerInstance } from '@kernel/logger/index.js'

// Events Hub
export { nexusEvents } from '@kernel/events/emitter.js'
export type { NexusEvents, NexusEventName, NexusEventPayload, DbEventPayload } from '@kernel/events/emitter.js'
export { eventBridge } from '@kernel/events/event-bridge.js'
export { entityRoom, isEntityRoom, parseEntityRoom } from '@kernel/events/room-utils.js'

// Errors
export {
  AppError,
  ValidationError,
  NotFoundError,
  UnauthorizedError,
  ForbiddenError,
  ConflictError
} from './errors/app-error.js'
export { ErrorCodes, type ErrorCode } from './errors/error-codes.js'

// Abilities (CASL)
export { defineAbilityFor, defineAnonymousAbility, packRules, unpackRules } from '@kernel/abilities/ability.factory.js'
export type { AppAbility, Actions, Subjects, SubjectStrings, SubjectRegistry } from '@kernel/abilities/ability.types.js'

// Middleware (Hono-native — lives in src/http/middleware/)
export { requireAbility } from '../http/middleware/ability.middleware.js'
export { createRateLimit, type RateLimitOptions } from '../http/middleware/rate-limit.middleware.js'
export { validateBody as validate } from '../http/middleware/validate.middleware.js'
export { errorHandler as errorMiddleware, initErrorMiddleware, type CaptureExceptionFn } from '../http/middleware/error.middleware.js'
export { requestTimeout } from '../http/middleware/timeout.middleware.js'

// Crypto
export { hashPassword, verifyPassword, DUMMY_HASH, encrypt, decrypt } from '@kernel/crypto/index.js'

// Socket.IO
export {
  getIO,
  initSocketIO,
  closeSocketIO,
  isSocketIOInitialized,
  emitToUser,
  emitToRoom,
  emitToRole,
  emitToAll,
  emitToAuthenticated,
  broadcastToRoom,
  getConnectedUsers,
  isUserConnected,
  getUserSocketCount,
  joinUserToRoom,
  removeUserFromRoom,
  joinSocketToRoom,
  removeSocketFromRoom,
  getRoomMembers,
  getRoomSize,
  getUserRooms,
  roomExists,
  onSocketReady
} from '@kernel/events/socket.js'

// SSE (Hono-native helpers live in src/http/sse.ts; batch-reporter is framework-agnostic)
export { createBatchReporter, type BatchReporter } from './sse/batch-reporter.js'

// Utils
export { generateId, generateIdByType, type IdType } from './utils/id.js'
export {
  generatePatternId,
  getNextSequence,
  ensureSequencesTable,
  SEQUENCES_TABLE,
  type PatternConfig
} from './utils/sequence.js'
export { getLibPath, getProjectPath, findEnvFile } from '@kernel/config/paths.js'
export { safeJsonParse } from './utils/safe-json.js'

// SPA Handler
export { resetServeSPA } from '../http/spa-handler.js'

// Cache
export { LRUCache, type LRUCacheOptions, type CacheStats } from '@kernel/cache/index.js'

// JWT (verification only - generation is in modules/auth/)
export { verifyAccessToken, type JwtPayload } from './jwt/index.js'

